import { Stack } from "@mui/material";
import { DeleteEmailCycleRPC } from "@pulse/pulse-rpcs/lib/email-cycle/deleteEmailCycleRPC";
import {
  Dialog,
  useFoundationColorTokens,
  useSpacing,
  useTypography,
} from "@surya-digital/leo-reactjs-material-ui";
import { HelpCircle } from "lucide-react";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { ProjectDetailsTabs } from "../../../projects/pages/ProjectDetailsPage";
import {
  processProjectParams,
  useRouteNavigator,
} from "../../../../routes/RoutesHelper";
import { useDeleteEmailCycleStore } from "../../stores/Hooks";
import { ErrorDialog } from "../../../../components/dialogs/ErrorDialog";

interface DeleteEmailCycleDialogProps {
  handleDialogClose: (refetchDetails?: boolean) => void;
}

type DeleteEmailCycleDescriptionI18nKey =
  | "emailCycles.thisEmailCycleDeletionDialogDescription"
  | "emailCycles.thisAndPendingEmailCycleDeletionDialogDescription";

type DialogToOpen =
  | "CONFIRM_DELETE"
  | "CYCLE_ALREADY_LAUNCH_ERROR"
  | "UNEXPECTED_ERROR";

const CONFIRM_DELETE_DIALOG_DESCRIPTION_ICON = 20;
const CONFIRM_DELETE_DIALOG_WIDTH = "560px";

export const DeleteEmailCycleDialog: React.FC<DeleteEmailCycleDialogProps> =
  observer(({ handleDialogClose }) => {
    const [dialogToOpen, setDialogToOpen] =
      useState<DialogToOpen>("CONFIRM_DELETE");

    const { t } = useTranslation();
    const spacing = useSpacing();
    const typography = useTypography();
    const tokens = useFoundationColorTokens();
    const navigateTo = useRouteNavigator();
    const deleteEmailCycleStore = useDeleteEmailCycleStore();

    const getDescriptionUIStringKey =
      (): DeleteEmailCycleDescriptionI18nKey => {
        switch (deleteEmailCycleStore.scope) {
          case DeleteEmailCycleRPC.RequestEnums.Scope.Scope.ONLY_THIS:
            return "emailCycles.thisEmailCycleDeletionDialogDescription";
          case DeleteEmailCycleRPC.RequestEnums.Scope.Scope.THIS_AND_PENDING:
            return "emailCycles.thisAndPendingEmailCycleDeletionDialogDescription";
          default:
            return deleteEmailCycleStore.scope satisfies never;
        }
      };

    const handleDeleteEmailCycle = async (): Promise<void> => {
      try {
        await deleteEmailCycleStore.deleteEmailCycle();

        switch (deleteEmailCycleStore.deleteEmailCycleRPCError) {
          case null:
            processProjectParams(
              async (projectId) =>
                navigateTo.projectDetails(
                  projectId,
                  ProjectDetailsTabs.EmailCycles,
                ),
              navigateTo,
            );
            break;
          case DeleteEmailCycleRPC.RPCError.InvalidScheduleId:
            navigateTo.pageNotFound();
            break;
          case DeleteEmailCycleRPC.RPCError.ScheduleInProgress:
            setDialogToOpen("CYCLE_ALREADY_LAUNCH_ERROR");
            break;
          default:
            deleteEmailCycleStore.deleteEmailCycleRPCError satisfies never;
        }
      } catch {
        setDialogToOpen("UNEXPECTED_ERROR");
      }
    };

    return (
      <>
        <Dialog
          open={dialogToOpen === "CONFIRM_DELETE"}
          title={t("emailCycles.deleteCycle")}
          primaryButtonText={t("emailCycles.deleteCycle")}
          primaryButtonColor="destructive"
          onPrimaryButtonClick={handleDeleteEmailCycle}
          secondaryButtonText={t("common.cancel")}
          onSecondaryButtonClick={() => handleDialogClose(false)}
          width={CONFIRM_DELETE_DIALOG_WIDTH}
          onClose={deleteEmailCycleStore.resetStore}
          disableBackdropClick={true}
        >
          <Stack
            direction="row"
            {...typography.b2}
            color={tokens.labelError}
            gap={spacing.spaceXS}
          >
            <HelpCircle size={CONFIRM_DELETE_DIALOG_DESCRIPTION_ICON} />
            <div style={{ width: "fit-content" }}>
              <Trans
                i18nKey={getDescriptionUIStringKey()}
                values={{
                  cycleName: deleteEmailCycleStore.emailCycleName.name,
                }}
                components={{
                  span: <span style={typography.s1} />,
                }}
              />
            </div>
          </Stack>
        </Dialog>

        {dialogToOpen === "CYCLE_ALREADY_LAUNCH_ERROR" && (
          <ErrorDialog
            title={t("emailCycles.deleteCycleFailed")}
            message={t("emailCycles.deleteCycleInProgressErrorMessage")}
            onClickClose={handleDialogClose}
          />
        )}

        {dialogToOpen === "UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t(
              "emailCycles.emailCycleDeleteUnexpectedErrorDialogDescription",
            )}
            onClickDone={handleDialogClose}
          />
        )}
      </>
    );
  });
